import { useState, useCallback, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { logger } from '../../lib/logger';
import { Button, Card, CardContent } from '@pantry/ui';
import { ArrowLeftIcon, QrCodeIcon, PlusIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import {
  useStorageLocations,
  useBulkCreateItems,
  materializeExternalProduct,
  apiFetch,
  type BulkCreateItem,
  type ProductSearchResult,
} from '@pantry/api-client';
import {
  validateBatchDraftItem,
  canMergeItems,
  extractInstancesForMerge,
  type BatchDraftItem,
  type BatchDraftInstance,
} from '@pantry/shared';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useBatchEntryStore } from '../../stores/batch-entry-store';
import { useBatchDraftPersistence } from '../../hooks/useBatchDraftPersistence';
import {
  BatchItemList,
  BatchItemEditSheet,
  BatchSummaryBar,
  ResumeDraftBanner,
  MultiSelectActionBar,
} from '../../components/batch-entry';
import { ContinuousBarcodeScanner } from '../../components/barcode/ContinuousBarcodeScanner';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/items_/batch')({
  component: BatchEntryPage,
});

function BatchEntryPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { selectedHousehold, isLoading: householdLoading } = useSelectedHousehold();
  const householdId = selectedHousehold?.id || '';

  // Store state
  const {
    isActive,
    draftItems,
    defaultStorageLocationId,
    isSubmitting,
    isMultiSelectMode,
    selectedClientIds,
    activate,
    deactivate,
    setDefaultStorageLocation,
    addDraftItem,
    updateDraftItem,
    removeDraftItem,
    addInstanceByBarcode,
    setSubmitting,
    setError,
    getDraftCount,
    enterMultiSelectMode,
    exitMultiSelectMode,
    toggleItemSelection,
    mergeSelectedItems,
    addInstanceToItem,
    removeInstance,
    updateInstance,
  } = useBatchEntryStore();

  // Persistence
  const { hasPendingDraft, pendingDraftCount, resumeDraft, discardDraft } =
    useBatchDraftPersistence();

  // UI state
  const [showScanner, setShowScanner] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);
  const [addingBatchToItemId, setAddingBatchToItemId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Data fetching
  const { data: storageLocations } = useStorageLocations(householdId);
  const bulkCreate = useBulkCreateItems();

  // Activate on mount if not already active
  useState(() => {
    if (!isActive && !hasPendingDraft) {
      activate();
    }
  });

  // Find the item being edited
  const editingItem = useMemo(() => {
    if (editingItemId) {
      return draftItems.find((item) => item.clientId === editingItemId) || null;
    }
    if (addingBatchToItemId) {
      return draftItems.find((item) => item.clientId === addingBatchToItemId) || null;
    }
    return null;
  }, [editingItemId, addingBatchToItemId, draftItems]);

  // Calculate valid item count
  const validCount = useMemo(() => {
    return draftItems.filter((item) => validateBatchDraftItem(item) === 'valid').length;
  }, [draftItems]);

  // Calculate if selected items can be merged
  const canMerge = useMemo(() => {
    if (selectedClientIds.size < 2) return false;
    const selectedItems = draftItems.filter((item) => selectedClientIds.has(item.clientId));
    if (selectedItems.length < 2) return false;
    const firstItem = selectedItems[0];
    return selectedItems.every((item) => canMergeItems(firstItem, item));
  }, [draftItems, selectedClientIds]);

  /**
   * Normalize barcode for searching.
   * Handles various barcode formats:
   * - EAN-13 (13 digits, may start with 0)
   * - UPC-A (12 digits)
   * - Barcodes with extra leading zeros from some scanners
   */
  const normalizeBarcode = (barcode: string): string[] => {
    const codes = new Set<string>();
    codes.add(barcode);

    // Strip all leading zeros and try that
    const stripped = barcode.replace(/^0+/, '');
    if (stripped.length >= 8) {
      codes.add(stripped);
    }

    // If we have 12 digits after stripping, that's likely UPC-A
    if (stripped.length === 12) {
      codes.add(stripped);
      // Also try with one leading 0 (EAN-13 format)
      codes.add('0' + stripped);
    }

    // If original is 13+ digits starting with 0, try without first 0
    if (barcode.length >= 13 && barcode.startsWith('0')) {
      codes.add(barcode.substring(1));
    }

    // If original is 14 digits starting with 00, try without first two 0s
    if (barcode.length === 14 && barcode.startsWith('00')) {
      codes.add(barcode.substring(2));
      codes.add(barcode.substring(1)); // Also try with just one 0 removed
    }

    logger.debug('Barcode normalization', { barcode, codes: Array.from(codes) });
    return Array.from(codes);
  };

  /**
   * Handle barcode scan - add new item or add instance to existing item.
   */
  const handleBarcodeScan = useCallback(
    async (barcode: string): Promise<boolean> => {
      // Check if barcode already exists in draft - add a new instance (batch)
      const normalizedCodes = normalizeBarcode(barcode);
      for (const code of normalizedCodes) {
        if (addInstanceByBarcode(code)) {
          toast.success(t`Added another batch`);
          return true;
        }
      }

      // Look up the product - try each barcode format
      for (const code of normalizedCodes) {
        try {
          const data = await apiFetch<ProductSearchResult>(
            `/households/${householdId}/items/search-products?barcode=${encodeURIComponent(code)}&includeExternal=true&limit=1`
          );

          // Check local results first
          if (data.local && data.local.length > 0) {
            const product = data.local[0];
            addDraftItem({
              productId: product.id,
              barcode: product.barcode || barcode,
              displayName: product.name,
              displayBrand: product.brand || undefined,
              displayImageUrl: product.imageUrl || undefined,
              isExternal: false,
              quantity: product.defaultQuantity || 1,
              unit: product.defaultUnit || 'item',
            });
            toast.success(t`Added: ${product.name}`);
            return true;
          }

          // Check external results
          if (data.external && data.external.length > 0) {
            const product = data.external[0];
            addDraftItem({
              barcode: product.barcode,
              displayName: product.name,
              displayBrand: product.brand || undefined,
              displayImageUrl: product.imageUrl || undefined,
              isExternal: true,
              quantity: product.defaultQuantity || 1,
              unit: product.defaultUnit || 'item',
            });
            toast.success(t`Added: ${product.name} (new)`);
            return true;
          }
        } catch (error) {
          logger.debug('Product lookup failed for barcode format', { code, error: String(error) });
          // Continue to try next barcode format
        }
      }

      // No results from any barcode format - add as unknown
      addDraftItem({
        barcode,
        displayName: t`Barcode: ${barcode}`,
        isExternal: false,
        quantity: 1,
        unit: 'item',
      });
      toast.info(t`Unknown barcode - tap to add details`);
      return true;
    },
    [householdId, addDraftItem, addInstanceByBarcode]
  );

  /**
   * Add a manual item (no barcode).
   */
  const handleAddManual = () => {
    const clientId = addDraftItem({
      customName: '',
      displayName: t`New Item`,
      isExternal: false,
      quantity: 1,
      unit: 'item',
    });
    setEditingItemId(clientId);
  };

  /**
   * Handle item edit save.
   * If the item is being assigned a barcode/productId that already exists in another item,
   * merge them by adding an instance to the existing item and removing the new one.
   */
  const handleEditSave = (updates: Partial<BatchDraftItem>) => {
    if (!editingItemId) return;

    const currentItem = draftItems.find((i) => i.clientId === editingItemId);
    if (!currentItem) return;

    // Check if we're setting a barcode/productId that matches another existing item
    const newBarcode = updates.barcode;
    const newProductId = updates.productId;

    if (newBarcode || newProductId) {
      const existingItem = draftItems.find((item) => {
        if (item.clientId === editingItemId) return false; // Skip self
        if (newProductId && item.productId === newProductId) return true;
        if (newBarcode && item.barcode === newBarcode) return true;
        return false;
      });

      if (existingItem) {
        // Merge: add instance(s) to existing item, remove the source item
        const instancesToAdd = extractInstancesForMerge(currentItem, updates);
        for (const instance of instancesToAdd) {
          addInstanceToItem(existingItem.clientId, instance);
        }

        removeDraftItem(editingItemId);
        toast.success(t`Added as another batch`);
        return;
      }
    }

    // No duplicate - just update normally
    updateDraftItem(editingItemId, updates);
  };

  /**
   * Handle instance save.
   */
  const handleSaveInstance = (instanceId: string, updates: Partial<BatchDraftInstance>) => {
    if (editingItemId) {
      updateInstance(editingItemId, instanceId, updates);
    }
  };

  /**
   * Handle adding a new batch to an existing item.
   */
  const handleAddBatch = (instance: Omit<BatchDraftInstance, 'instanceId'>) => {
    if (addingBatchToItemId) {
      addInstanceToItem(addingBatchToItemId, instance);
    }
  };

  /**
   * Handle merge action.
   */
  const handleMerge = () => {
    if (mergeSelectedItems()) {
      toast.success(t`Items merged`);
    } else {
      toast.error(t`Cannot merge items with different products or units`);
    }
  };

  /**
   * Submit the batch.
   */
  const handleSubmit = async () => {
    if (!householdId) return;

    const validItems = draftItems.filter((item) => validateBatchDraftItem(item) === 'valid');

    if (validItems.length === 0) {
      toast.error(t`No valid items to add`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Materialize external products first, then flatten instances
      const itemsToCreate: BulkCreateItem[] = [];

      for (const item of validItems) {
        let productId = item.productId;

        // If external, materialize the product
        if (item.isExternal && item.barcode && !productId) {
          try {
            const product = await materializeExternalProduct(item.barcode);
            productId = product.id;
          } catch (err) {
            logger.error('Failed to materialize product', err, { barcode: item.barcode });
            // Continue without productId - will use customName
          }
        }

        // Flatten instances into individual items for the API
        for (const instance of item.instances) {
          itemsToCreate.push({
            productId: productId || undefined,
            storageLocationId: item.storageLocationId || defaultStorageLocationId || undefined,
            customName: !productId ? item.displayName : undefined,
            quantity: instance.quantity,
            unit: item.unit,
            expiryDate: instance.expiryDate || undefined,
            purchaseDate: instance.purchaseDate || undefined,
            notes: instance.notes || undefined,
          });
        }
      }

      const result = await bulkCreate.mutateAsync({
        householdId,
        data: {
          items: itemsToCreate,
          defaultStorageLocationId: defaultStorageLocationId || undefined,
        },
      });

      if (result.failed > 0) {
        toast.warning(t`${result.successful} added, ${result.failed} failed`);
      } else {
        toast.success(t`${result.successful} items added to pantry`);
      }

      // Clear and navigate back
      deactivate();
      navigate({ to: '/items' });
    } catch (error) {
      logger.error('Bulk create failed', error, {
        householdId: selectedHousehold?.id,
        itemCount: validItems.length,
      });
      setError(error instanceof Error ? error.message : t`Failed to add items`);
      toast.error(t`Failed to add items`);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Discard the batch with confirmation.
   */
  const handleDiscard = () => {
    if (draftItems.length > 0) {
      if (!confirm(t`Discard ${draftItems.length} items?`)) {
        return;
      }
    }
    deactivate();
    navigate({ to: '/items' });
  };

  // Close the edit sheet
  const handleCloseEditSheet = () => {
    setEditingItemId(null);
    setEditingInstanceId(null);
    setAddingBatchToItemId(null);
  };

  // Loading state
  if (householdLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  // No household selected
  if (!selectedHousehold) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="rounded-xl" asChild>
            <Link to="/items">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              <Trans>Back</Trans>
            </Link>
          </Button>
        </div>
        <Card className="rounded-2xl">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              <Trans>Select a household to add items.</Trans>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={handleDiscard}>
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            <Trans>Back</Trans>
          </Button>
          <div>
            <h1 className="font-display text-xl font-semibold text-foreground">
              <Trans>Add Multiple Items</Trans>
            </h1>
            <p className="text-sm text-muted-foreground">
              <Trans>Scan or add items, then submit all at once</Trans>
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-xl h-9 w-9 p-0"
          onClick={() => setShowSettings(!showSettings)}
        >
          <Cog6ToothIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* Resume draft banner */}
      {hasPendingDraft && draftItems.length === 0 && (
        <ResumeDraftBanner
          itemCount={pendingDraftCount}
          onResume={resumeDraft}
          onDiscard={discardDraft}
        />
      )}

      {/* Settings panel */}
      {showSettings && (
        <Card className="rounded-xl animate-in fade-in slide-in-from-top-2 duration-200">
          <CardContent className="py-4">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium" htmlFor="defaultLocation">
                  <Trans>Default Storage Location</Trans>
                </label>
                <select
                  id="defaultLocation"
                  value={defaultStorageLocationId || ''}
                  onChange={(e) => setDefaultStorageLocation(e.target.value || null)}
                  className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border"
                >
                  <option value="">{t`None (set per item)`}</option>
                  {storageLocations?.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button
          onClick={() => setShowScanner(true)}
          className="flex-1 h-11 rounded-xl"
          variant="default"
        >
          <QrCodeIcon className="h-5 w-5 mr-2" />
          <Trans>Scan Barcodes</Trans>
        </Button>
        <Button onClick={handleAddManual} variant="outline" className="h-11 rounded-xl">
          <PlusIcon className="h-5 w-5 mr-2" />
          <Trans>Add Manual</Trans>
        </Button>
      </div>

      {/* Item list */}
      <BatchItemList
        items={draftItems}
        isMultiSelectMode={isMultiSelectMode}
        selectedClientIds={selectedClientIds}
        onEditItem={(clientId) => {
          setEditingItemId(clientId);
          setEditingInstanceId(null);
          setAddingBatchToItemId(null);
        }}
        onRemoveItem={removeDraftItem}
        onToggleSelect={(clientId) => {
          if (!isMultiSelectMode) {
            enterMultiSelectMode();
          }
          toggleItemSelection(clientId);
        }}
        onEditInstance={(clientId, instanceId) => {
          setEditingItemId(clientId);
          setEditingInstanceId(instanceId);
          setAddingBatchToItemId(null);
        }}
        onDeleteInstance={(clientId, instanceId) => {
          removeInstance(clientId, instanceId);
        }}
        onUpdateInstanceQuantity={(clientId, instanceId, quantity) => {
          updateInstance(clientId, instanceId, { quantity });
        }}
        onAddBatch={(clientId) => {
          setAddingBatchToItemId(clientId);
          setEditingItemId(null);
          setEditingInstanceId(null);
        }}
      />

      {/* Edit sheet */}
      <BatchItemEditSheet
        item={editingItem}
        householdId={householdId}
        isOpen={!!editingItem}
        onClose={handleCloseEditSheet}
        onSave={handleEditSave}
        editingInstanceId={editingInstanceId}
        onSaveInstance={handleSaveInstance}
        isAddingBatch={!!addingBatchToItemId}
        onAddBatch={handleAddBatch}
      />

      {/* Continuous scanner */}
      {showScanner && (
        <ContinuousBarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
          itemCount={getDraftCount()}
        />
      )}

      {/* Multi-select action bar */}
      {isMultiSelectMode && (
        <MultiSelectActionBar
          selectedCount={selectedClientIds.size}
          canMerge={canMerge}
          onMerge={handleMerge}
          onCancel={exitMultiSelectMode}
        />
      )}

      {/* Summary bar (hide when in multi-select mode) */}
      {draftItems.length > 0 && !isMultiSelectMode && (
        <BatchSummaryBar
          itemCount={draftItems.length}
          validCount={validCount}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onDiscard={handleDiscard}
        />
      )}
    </div>
  );
}
